import type { WalletType } from '@/types'

export interface DetectedWallet {
  type: WalletType
  name: string
  provider: EthereumProvider
  icon?: string
}

// Minimal EIP-1193 provider interface
export interface EthereumProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>
  on: (event: string, handler: (...args: unknown[]) => void) => void
  removeListener: (event: string, handler: (...args: unknown[]) => void) => void
  isMetaMask?: boolean
  isRabby?: boolean
  isDCent?: boolean
  isCoinbaseWallet?: boolean
  isBraveWallet?: boolean
  providers?: EthereumProvider[]
}

/**
 * Detect all injected EVM wallets without WalletConnect.
 * Rabby overrides window.ethereum, so we check its flag first.
 * Fallback: any window.ethereum-compatible injected provider.
 */
export function detectInjectedWallets(): DetectedWallet[] {
  if (typeof window === 'undefined') return []

  const wallets: DetectedWallet[] = []
  const seen = new Set<EthereumProvider>()

  // EIP-6963 multi-wallet support (new standard)
  // providers array (MetaMask + Rabby etc. both inject)
  const eth = window.ethereum
  if (!eth) return wallets

  // Some providers expose a list (MetaMask + Coinbase inject .providers)
  const providerList: EthereumProvider[] = eth.providers ?? [eth]

  for (const provider of providerList) {
    if (seen.has(provider)) continue
    seen.add(provider)

    if (provider.isRabby) {
      wallets.push({ type: 'rabby', name: 'Rabby Wallet', provider })
    } else if (provider.isMetaMask) {
      wallets.push({ type: 'metamask', name: 'MetaMask', provider })
    } else if (provider.isDCent) {
      wallets.push({ type: 'dcent', name: "D'CENT Wallet", provider })
    } else {
      wallets.push({ type: 'injected', name: 'Injected Wallet', provider })
    }
  }

  return wallets
}

export function getPreferredProvider(type?: WalletType): EthereumProvider | null {
  const wallets = detectInjectedWallets()
  if (!wallets.length) return null
  if (!type) return wallets[0].provider
  return wallets.find((w) => w.type === type)?.provider ?? wallets[0].provider
}
